import dataUtils from '@/common/dataUtils';
import { performInMemorySearch } from '@/components/search/searchStore';
import { cacheAllModuleFiles, updateModuleCache } from '@/store/sync/cachingUtils';
import { SYNC_STATES } from '@/store/sync/sync';
import newsApi from './newsApi';
import { push } from '../../common/utils';
import moduleIndex from './index.js';
import MODULE_ID from './moduleId';
import getters from './store.getters';
import storeUtil from './storeUtil';
import favoritesService from '@/services/favoritesService';
import storeService from '@/services/storeService';

const ROOT = { root: true };
const IN_MEMORY_DB = {}; // Currently all states are stored in vuex store
const USER_STATE_TYPE = 'newsUserState';

const state = {
  news: [], // news items ordered by anzeige_von desc
  newsById: {},
  labels: [],
  usedLabels: [],
  labelsById: {},
  labelsNewsMapping: {},
  labelsByInstance: {},
  medias: [],
  mediasById: {},
  userState: [],

  // --- UI states
  dataLoaded: false,
  unreadCountsByInstance: {},
  selectedLabelId: null,
  instance: null,
  showUnreadOnly: false,
};

const mutations = {
  setSelectedLabelId(state, value) {
    state.selectedLabelId = value;
  },
  setShowUnreadOnly(state, value) {
    state.showUnreadOnly = value;
  },
  setNewsToFavorite(state, { id, instance, _userState }) {
    const obj = state.newsById[id]._userState;
    state.newsById[id]._userState = Object.assign({}, obj);
    // console.debug('inda store setNewsToFavorite', state.newsById[id]._userState.favorite);
  },
  setNewsData(state, serverData) {
    storeService.saveDataToInMemoryStore(MODULE_ID, state, IN_MEMORY_DB, serverData);
    state.news = serverData.news;
    state.newsById = serverData.newsById;
    state.labels = serverData.labels;
    state.labelsById = serverData.labelsById;
    state.labelsNewsMapping = serverData.labelsNewsMapping;
    state.labelsByInstance = serverData.labelsByInstance;
    state.medias = serverData.medias;
    state.mediasById = serverData.mediasById;
    state.userState = serverData.userState;
    state.usedLabels = serverData.usedLabels;

    state.dataLoaded = true;
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {
    let unreadCountsByInstance = storeUtil.countItems({
      news: state.news,
      labels: state.labels,
      labelsById: state.labelsById,
    });
    state.unreadCountsByInstance = unreadCountsByInstance;
  },
};
const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this getter to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all documents containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  searchModuleInMemoryContainingSearchTerm({ state }, { filterCriteria, options }) {
    return performInMemorySearch({
      moduleId: MODULE_ID,
      items: state.news,
      titleSearchAttr: 'title',
      contentSearchAttr: 'content',
      searchCriteriaArray: filterCriteria,
      options,
    });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  // TODO: DELETE as soon as nowhere used
  async updateModuleApplicationCache({ state }) {
    // const { news, labels, labelsNewsMapping, medias, userState } = state;
    // await updateModuleCache(moduleIndex.api, { news, labels, labelsNewsMapping, medias, userState });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeFiles({ commit, dispatch, state }) {
    await cacheAllModuleFiles({ files: state.medias, moduleIndex, commit, dispatch });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeData({ commit, dispatch, rootState, rootGetters }) {
    try {
      let response = await newsApi.getNews();
      let { news, labels, labelsNewsMapping, medias, userState } = response.data;

      let labelsByInstance = {};
      let unreadCountsByInstance = {};

      const labelsById = dataUtils.buildIdMap(labels, 'id');
      const newsById = dataUtils.buildIdMap(news, 'id');
      const mediasById = dataUtils.buildIdMap(medias, 'id');

      // Create and sort labels by instance lists
      labels.forEach((label) => push(labelsByInstance, label.instance, label));
      Object.keys(labelsByInstance).forEach((instance) => labelsByInstance[instance].sort((a, b) => Number(a.sort) - Number(b.sort)));

      const userStateByNewsId = dataUtils.buildIdMap(userState, 'newsIdFk');

      // Create map and enhance with necessary  data
      news
        .filter((newsItem) => !!newsItem)
        .forEach((newsItem) => {
          //enhance item with information used when searching
          let moduleConfig = rootGetters['configs/getModuleConfig']({
            moduleType: moduleIndex.moduleType,
            instance: newsItem.instance,
          });
          if (!moduleConfig) return;

          // add calculated data used mainly for displaying in live-search
          newsItem._displayLocation = '';
          newsItem._moduleTitle = moduleConfig.title;
          newsItem._moduleId = MODULE_ID;
          newsItem._path = `/news/${newsItem.instance}/${newsItem.id}`;

          // add userState
          if (userStateByNewsId[newsItem.id]) {
            newsItem._userState = userStateByNewsId[newsItem.id];
          }
          if (!newsItem._userState) {
            // IMPORTANT: Be sure to set all properties which can be changed and should be watched!!!!!
            newsItem._userState = { dateread: '', newsIdFk: newsItem.id, favorite: 0 };
          }
          favoritesService.assertFavoriteIsSet(newsItem);
          newsItem._userState._type = USER_STATE_TYPE;
        });
      favoritesService.addFavoriteStatesFromLocalStorage({ moduleId: MODULE_ID, items: news });

      // apply user states stored in localStorage if the userState is newer than what is stored on the item.
      // Delete from local storage if we have a newer state on the object
      // Do not delete otherwise as it is obvious now, the state has not been stored on backend.
      dataUtils.applyLocalStorageUserState(newsById, USER_STATE_TYPE, 'newsIdFk');

      // Associate labels and create usedLabelsMap
      let usedLabels = {};
      labelsNewsMapping.forEach((label) => {
        let news = newsById[label.newsIdFk];
        if (news) {
          usedLabels[label.labelIdFk] = labelsById[label.labelIdFk];
        }
        push(news, '_labels', label);
      });

      // Add calculated data for live-search within attachements
      // separate medias into pdfs and teasers
      medias.forEach((media) => {
        let news = newsById[media.newsIdFk];
        if (media.type === 'pdf') {
          push(news, '_pdfs', media);
        } else if (media.type === 'teaser') {
          push(news, '_teasers', media);
        }

        // add calculated data used mainly for displaying in live-search
        let newsItemBase = newsById[media.newsIdFk];
        if (newsItemBase) {
          let moduleConfig = rootGetters['configs/getModuleConfig']({
            moduleType: moduleIndex.moduleType,
            instance: newsItemBase.instance,
          });
          if (moduleConfig) {
            media._displayLocation = `Anhang <b>${newsItemBase.title}</b>`;
            media._moduleTitle = moduleConfig.title;

            media._path = `/news/${newsItemBase.instance}/${newsItemBase.id}/${media.id}`;
          }
        }
      });

      commit('setNewsData', {
        news,
        userState,
        newsById,
        labels,
        labelsById,
        labelsNewsMapping,
        labelsByInstance,
        unreadCountsByInstance,
        medias,
        mediasById,
        usedLabels: Object.keys(usedLabels).map((key) => usedLabels[key]),
      });
    } catch (err) {
      console.error(err);
      commit('sync/setSyncState', { moduleId: MODULE_ID, syncState: SYNC_STATES.FAILURE, msg: err }, ROOT);
      throw err;
    }
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
